import { Injectable, inject } from '@angular/core';
import { ICredentials } from '@okcargo-apps/shared-domain-ionic';
import { ConnectivityService, JwtService } from '@okcargo-apps/shared-infrastructure';
import { AccountCommandService } from '@okcargo-apps/shared-infrastructure-ionic';
import { Observable, firstValueFrom, tap } from 'rxjs';
import { AccountService } from '../account';

/**
 * State of the stored session after resolving it. It is the single input the guards use to decide
 * where the user goes, so they do not repeat (and invert) the same logic.
 *
 * - `no-credentials`: there is no session to trust, whatever the account snapshot says.
 * - `verified`: the session is (or has just been) verified against the backend.
 * - `unverified`: there is a session, but it could not be verified yet (no connection, or the
 *   connection is not usable). The account snapshot decides and the verification is retried.
 * - `rejected`: the backend rejected the session.
 */
export type SessionResolution = 'no-credentials' | 'verified' | 'unverified' | 'rejected';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly jwtService = inject(JwtService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly accountCommandService = inject(AccountCommandService);
  private readonly accountService = inject(AccountService);

  /**
   * Verifies the stored session. Returns whether it could be verified: until it is, the app must not
   * consider the user as logged in (the account snapshot is only a hint).
   */
  public async initializeSession(): Promise<boolean> {
    const verified = await this.accountService.checkUserAuthentication();

    if (verified) {
      this.jwtService.setUserDataReceived(true);
    }

    return verified;
  }

  /**
   * Resolves the state of the stored session, verifying it when it has to be verified.
   *
   * The precedence is the one the guards had (it matters): an already verified session wins over the
   * token of the URL (the current session is still good), and the token wins over an expired stored
   * session (the verification would be wasted, and it could even clear the session the token comes to
   * replace).
   */
  public async resolveSession(urlToken?: string | null): Promise<SessionResolution> {
    const { accessToken, refreshToken } = this.jwtService;

    if (accessToken || refreshToken) {
      if (await firstValueFrom(this.jwtService.userDataReceived$)) {
        return 'verified';
      }
    } else if (!urlToken) {
      return 'no-credentials';
    }

    if (urlToken) {
      await this.validateToken(urlToken);

      return 'verified';
    }

    if (this.connectivity.isOffline()) {
      // Without connectivity the session cannot be verified (the check would wait for the connection):
      // it is left running and the account snapshot hydrated at startup decides until then.
      void this.initializeSession().catch(() => undefined);

      return 'unverified';
    }

    if (await this.initializeSession()) {
      return 'verified';
    }

    // The verification failed. When the backend rejects the session the interceptor clears the
    // credentials (and the account snapshot with them), so without credentials the session is
    // rejected. With the credentials still there it was a transient failure (a server error, or a
    // connection the platform reports as available but that is not usable), which must not log the
    // user out: the snapshot decides and the verification is retried.
    const hasCredentials = !!(this.jwtService.accessToken || this.jwtService.refreshToken);

    return hasCredentials ? 'unverified' : 'rejected';
  }

  public async login(credentials: ICredentials): Promise<void> {
    await firstValueFrom(
      this.jwtService.login$(credentials).pipe(
        tap((response) => {
          this.jwtService.setAccessToken(response.access_token || response.accessToken);
          this.jwtService.setRefreshToken(response.refresh_token || response.refreshToken);
        }),
      ),
    );
    await this.initializeSession();
  }

  /**
   * Starts the session that a token received by link grants, replacing whatever session was open.
   *
   * The stored session is dropped **before** exchanging the token, on purpose: it belongs to another
   * user, so it must not travel with the request (an authenticated request would add the previous
   * `Bearer`, and if the previous user is one of those redirected to another environment it would even
   * send the exchange to the wrong backend). It must not decide the navigation either: with the old
   * session still open, its account capabilities were the ones used to resolve the session.
   *
   * The stored `username` is not touched: it is only the last user who logged in explicitly, used to
   * prefill the login form.
   */
  public async validateToken(token: string): Promise<void> {
    this.jwtService.clearTokens();
    this.accountService.clearIdentity();

    await firstValueFrom(
      this.jwtService.validateToken$(token).pipe(
        tap((response) => {
          this.jwtService.setAccessToken(response.access_token || response.accessToken);
          this.jwtService.setRefreshToken(response.refresh_token || response.refreshToken);
        }),
      ),
    );
    await this.initializeSession();
  }

  public async logout(): Promise<void> {
    try {
      await firstValueFrom(this.jwtService.logout$());
    } catch (e) {
      console.warn('Error on server logout, cleaning local session anyway');
    } finally {
      this.jwtService.clearTokens();
      this.accountService.clearIdentity();
    }
  }

  public changePasswordRequest(email: string): Observable<void> {
    return this.accountCommandService.changePasswordRequest(email);
  }

  public changePasswordConfirm(email: string, newPassword: string, key: string): Observable<void> {
    return this.accountCommandService.changePasswordConfirm(email, newPassword, key);
  }

  public changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.accountCommandService.changePassword(currentPassword, newPassword);
  }
}
